import { resolveAddressLocale, type AddressLocale } from './addressTranslations';

type Plural = { one: string; few?: string; many?: string; other: string };

// Checkout pages are localised from the page's locale (useCheckoutLocalization),
// not i18next, which only the customer portal mounts. These follow the address
// label tables so a translated page never shows an English validation message.
const selectAtLeast: Record<AddressLocale, Plural> = {
  en: { one: 'Select at least {{count}} option', other: 'Select at least {{count}} options' },
  de: { one: 'Wähle mindestens {{count}} Option', other: 'Wähle mindestens {{count}} Optionen' },
  es: {
    one: 'Selecciona al menos {{count}} opción',
    other: 'Selecciona al menos {{count}} opciones',
  },
  fr: {
    one: 'Sélectionnez au moins {{count}} option',
    other: 'Sélectionnez au moins {{count}} options',
  },
  it: { one: 'Seleziona almeno {{count}} opzione', other: 'Seleziona almeno {{count}} opzioni' },
  nl: { one: 'Kies minstens {{count}} optie', other: 'Kies minstens {{count}} opties' },
  pl: {
    one: 'Wybierz co najmniej {{count}} opcję',
    few: 'Wybierz co najmniej {{count}} opcje',
    many: 'Wybierz co najmniej {{count}} opcji',
    other: 'Wybierz co najmniej {{count}} opcji',
  },
  'pt-BR': {
    one: 'Selecione pelo menos {{count}} opção',
    other: 'Selecione pelo menos {{count}} opções',
  },
  'pt-PT': {
    one: 'Selecione pelo menos {{count}} opção',
    other: 'Selecione pelo menos {{count}} opções',
  },
};

const selectAtMost: Record<AddressLocale, Plural> = {
  en: { one: 'Select at most {{count}} option', other: 'Select at most {{count}} options' },
  de: { one: 'Wähle höchstens {{count}} Option', other: 'Wähle höchstens {{count}} Optionen' },
  es: {
    one: 'Selecciona como máximo {{count}} opción',
    other: 'Selecciona como máximo {{count}} opciones',
  },
  fr: {
    one: 'Sélectionnez au plus {{count}} option',
    other: 'Sélectionnez au plus {{count}} options',
  },
  it: {
    one: 'Seleziona al massimo {{count}} opzione',
    other: 'Seleziona al massimo {{count}} opzioni',
  },
  nl: { one: 'Kies hoogstens {{count}} optie', other: 'Kies hoogstens {{count}} opties' },
  pl: {
    one: 'Wybierz co najwyżej {{count}} opcję',
    few: 'Wybierz co najwyżej {{count}} opcje',
    many: 'Wybierz co najwyżej {{count}} opcji',
    other: 'Wybierz co najwyżej {{count}} opcji',
  },
  'pt-BR': {
    one: 'Selecione no máximo {{count}} opção',
    other: 'Selecione no máximo {{count}} opções',
  },
  'pt-PT': {
    one: 'Selecione no máximo {{count}} opção',
    other: 'Selecione no máximo {{count}} opções',
  },
};

function pluralForm(locale: AddressLocale, count: number): keyof Plural {
  if (count === 1) return 'one';
  if (locale === 'pl') {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'few';
    return 'many';
  }
  return 'other';
}

function translateCount(
  table: Record<AddressLocale, Plural>,
  locale: string | undefined,
  count: number,
): string {
  const resolved = resolveAddressLocale(locale);
  const forms = table[resolved];
  const form = pluralForm(resolved, count);
  return (forms[form] ?? forms.other).replace('{{count}}', String(count));
}

export function translateSelectAtLeast(locale: string | undefined, count: number): string {
  return translateCount(selectAtLeast, locale, count);
}

export function translateSelectAtMost(locale: string | undefined, count: number): string {
  return translateCount(selectAtMost, locale, count);
}
